import type { DomJson } from '@nx/constants';
import { TEST_GUIDE_PAGES } from '@nx/constants';
import { DEFAULT_ALLOWED_HOSTS } from '@nx/neo4j-sdk';
import { URLs, isNotNullish } from '@nx/stdlib';
import type { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { asyncThunkCreator, buildCreateSlice } from '@reduxjs/toolkit';

import { addNotification } from '../notifications-slice';
import customGuideTransformer from './transformers/custom-guide';

export interface SelectGuideParams {
  id: string;
  title: string;
  url?: string;
  localGuide?: string;
  custom?: boolean;
  allowedHosts?: string[];
}

export interface SetGuideCurrentPageParams {
  id: string;
  pageNumber: number;
}

export type Guide = {
  id: string;
  title: string;
  url?: string;
  localGuide?: string;
  contents: DomJson[];
  totalPages: number;
};

export type GuideStatus = {
  currentPage: number;
  isCompleted: boolean;
};

export type Guides = Record<string, Guide>;
export type GuidesStatuses = Record<string, GuideStatus>;

export interface GuidesState {
  guides: Guides;
  guidesStatuses: GuidesStatuses;
  currentGuideId: string | undefined;
  // don't use Storage API until there is a user action or UI visible which requires it
  skipApi: boolean;

  // TODO: move status and error into a guide?
  fetchStatus?: 'error' | 'loading' | 'success';
  fetchError?: SerializedError;
}

export const GUIDES_PERSISTED_KEYS: (keyof GuidesState)[] = ['guides', 'guidesStatuses', 'currentGuideId'];

const fetchRemoteGuide = async (
  urlOrGuideID: string,
  allowedHosts: string[],
  disableCache: boolean | undefined = false,
) => {
  if (!URLs.isValidURL(urlOrGuideID)) {
    const attempts = allowedHosts.map((host) =>
      fetch(`${location.protocol}//${host}/${urlOrGuideID}`, disableCache ? { cache: 'no-cache' } : undefined),
    );

    const response = await Promise.any(attempts);
    if (!response.ok) {
      throw new Error(`Failed to fetch guide from ${urlOrGuideID}`);
    }
    const text = await response.text();
    return text;
  }
  const response = await fetch(urlOrGuideID, disableCache ? { cache: 'no-cache' } : undefined);
  const text = await response.text();
  return text;
};

const initialState: GuidesState = {
  guides: {},
  guidesStatuses: {},
  currentGuideId: undefined,
  skipApi: true,
};

const createSliceWithThunks = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator },
});

const hasMessage = (error: unknown): error is { message: string } => {
  return typeof error === 'object' && error !== null && 'message' in error;
};

const guidesSlice = createSliceWithThunks({
  name: 'guides',
  initialState,
  reducers: (create) => ({
    triggerUseApi: create.reducer((state) => {
      state.skipApi = false;
    }),
    deselectGuide: create.reducer((state) => {
      state.currentGuideId = undefined;
    }),
    createGuideStatus: create.reducer((state, action: PayloadAction<{ id: string } & GuideStatus>) => {
      const { id, ...status } = action.payload;
      state.guidesStatuses[id] = status;
    }),
    deleteGuideStatus: create.reducer((state, action: PayloadAction<string>) => {
      const id = action.payload;
      delete state.guidesStatuses[id];
    }),
    setGuideCurrentPage: create.reducer((state, action: PayloadAction<SetGuideCurrentPageParams>) => {
      const { id, pageNumber } = action.payload;
      const guide = state.guides[id];
      const guideStatus = state.guidesStatuses[id];

      if (guide === undefined || guideStatus === undefined) {
        return;
      }

      // clamp the page number to the total number of pages
      const currentPage = Math.min(pageNumber, guide.totalPages - 1);

      // When the user navigates to the last page, we mark it as completed.
      if (currentPage === guide.totalPages - 1) {
        guideStatus.isCompleted = true;
      }

      guideStatus.currentPage = currentPage;
    }),
    selectGuide: create.asyncThunk(
      async (
        { id, title, url, localGuide, custom = false, allowedHosts = DEFAULT_ALLOWED_HOSTS }: SelectGuideParams,
        { rejectWithValue, dispatch },
      ) => {
        const noCache = custom || id === TEST_GUIDE_PAGES.NX_INTERNAL_TEST.toString();

        let text: string | undefined;

        try {
          text = localGuide ?? (await fetchRemoteGuide(url ?? '', allowedHosts, noCache));
        } catch {
          dispatch(
            addNotification({
              title: 'Failed to fetch the guide',
              description: isNotNullish(url) ? `Failed to load the guide ${url}` : '',
              type: 'danger',
            }),
          );

          return rejectWithValue({ message: 'Failed to fetch the guide', url });
        }

        if (!custom) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const guideContents: DomJson[] = JSON.parse(text);
          return { id, title, url, contents: guideContents };
        }

        try {
          if (url?.endsWith('.adoc') === true) {
            // Avoids loading asciidoctor unless required
            const { parseGuideOnline } = await import('@neo4j-devtools/workspace-guides/src/parse-guide-online');

            const urlObj = new URL(url);
            const baseUrl = `${urlObj.origin}${urlObj.pathname.split('/').slice(0, -1).join('/')}/`;

            const { contents, title: customTitle } = parseGuideOnline(text, baseUrl);
            return { id, title: customTitle, url, contents };
          }

          const { title: customTitle, contents: customContents } =
            customGuideTransformer.transformHtmlToTitleAndContents(text);
          return { id, title: customTitle, url, contents: customContents };
        } catch (e: unknown) {
          const message = hasMessage(e) ? e.message : 'Failed to parse the guide';
          dispatch(
            addNotification({
              title: message,
              description: isNotNullish(url) ? `Failed to load the guide ${url}` : '',
              type: 'danger',
            }),
          );
          return rejectWithValue({ message, url });
        }
      },

      {
        pending: (state) => {
          state.fetchStatus = 'loading';
          state.fetchError = undefined;
        },
        rejected: (state, action) => {
          state.fetchStatus = 'error';
          state.fetchError = action.error;
          state.currentGuideId = undefined;
        },
        fulfilled: (state, action) => {
          state.fetchStatus = 'success';
          state.fetchError = undefined;

          const { id, contents } = action.payload;

          state.guides[id] = {
            ...action.payload,
            totalPages: contents.length,
          };

          state.currentGuideId = id;
        },
      },
    ),
  }),
});

export const { triggerUseApi, deselectGuide, createGuideStatus, deleteGuideStatus, setGuideCurrentPage, selectGuide } =
  guidesSlice.actions;
export default guidesSlice.reducer;
